import React, { useState, forwardRef, useCallback, useEffect } from 'react';
import { darkStyles, lightStyles } from './styles.js';
import { detectLanguage } from './detectlang.js';
import { AceEditorComponent } from './AceSpaceBro.js';
import { RenderCopyToClipboardButton } from './copyclip.js';
import VectorVault from 'vectorvault';
import './App.css';

export function ChatContainer({
  isDarkMode,
  isMobile,
  setUserCredits,
  setIsLoggedIn,
  showUpgradeMessage,
  setShowUpgradeMessage,
  chatHistory,
  setChatHistory,
  openSignIn,
  openSignUp,
  gpt4,
  windowWidth,
  userRefdb,
  userData,
  currentUser,
  update
}, ref) {

  const [chatMessage, setChatMessage] = useState('');
  const [isChatLoading, setChatIsLoading] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [summary, setSummary] = useState(false);
  const [getContext, setGetContext] = useState(true);
  const [nContext, setNContext] = useState(3);
  const [historySearch, setHistorySearch] = useState(false);
  const [model, setModel] = useState('');
  const [tempMessage, setTempMessage] = useState('');
  const [includeContextMeta, setIncludeContextMeta] = useState(false);
  const [vectorVault, setVectorVault] = useState(null);
  
  // VectorVault configuration
  const VECTOR_VAULT_CONFIG = {
    vault: 'philosophy',
    email: "MQ[a&_iU^XWoibH",
    deploymentId: "New Dep"
  };

  const styles = isDarkMode ? darkStyles : lightStyles;

  // Initialize VectorVault on component mount
  useEffect(() => {
    const initVectorVault = async () => {
      try {
        const vv = new VectorVault();
        
        // Initialize with deployment credentials
        await vv.initializeDeployment(VECTOR_VAULT_CONFIG.email, VECTOR_VAULT_CONFIG.deploymentId);
        console.log('VectorVault initialized successfully with deployment ID:', VECTOR_VAULT_CONFIG.deploymentId);
        setVectorVault(vv);
      } catch (error) {
        console.error('Failed to initialize VectorVault:', error);
      }
    };

    initVectorVault();
  }, []);

  const copyToClipboard = () => {
    setIsCopied(true);
    setTimeout(() => setIsCopied(false), 3000);
  };

  const showChatLoadingSpinner = useCallback(() => {
    setChatIsLoading(true);
  }, [setChatIsLoading]);

  const hideChatLoadingSpinner = useCallback(() => {
    setChatIsLoading(false);
  }, [setChatIsLoading]);

  const appendChatMessage = useCallback((message, isUserMessage) => {
    setChatHistory((prevHistory) => [
      ...prevHistory,
      { message, isUserMessage },
    ]);
  }, [setChatHistory]);

  const removeLastChatMessage = useCallback(() => {
    setChatHistory((prevHistory) => {
      if (prevHistory.length > 0 && !prevHistory[prevHistory.length - 1].isUserMessage) {
        const updatedHistory = prevHistory.slice(0, prevHistory.length - 1);
        return updatedHistory;
      }
      return prevHistory;
    });
  }, [setChatHistory]);

  function renderChatSegment(segment, index, isUserMessage) {
    const ram = '```';
    if (segment.startsWith(ram) && segment.endsWith(ram)) {
      const code = segment.slice(3, -3);
      const type = detectLanguage(code);

      const increaseBy = 1/(windowWidth/500) * 6;

      return (
        <div key={index} style={{ position: 'relative', width: `10${increaseBy}%`, marginLeft: '-13px' }}>
          <label>{type}</label>
          <AceEditorComponent code={code} type={type} isDarkMode={isDarkMode} />
          {RenderCopyToClipboardButton(code, isDarkMode, isCopied, setIsCopied, copyToClipboard)}
        </div>
      );
    } else {
      return (
        <div
          key={index}
          id="chat-history"
          style={isUserMessage ? styles.userMessage : styles.botMessage}
        >
          {segment}
        </div>
      );
    }
  }

  function UpgradeMessage() {
    return (
      <div>
        <p>Serving PhilosophyGPT isn't free. Sign up to continue:</p>
        <stripe-buy-button
          buy-button-id="buy_btn_1MrtDZET5bakQRi3AO3mk56T"
          publishable-key={process.env.REACT_APP_STRIPE_PUB_KEY}
        >
        </stripe-buy-button>
      </div>
    );
  }

  const sendChatMessage = async () => {
    console.log('sendChatMessage invoked');
    if (!chatMessage) return;

    // Immediately append user's message to the chat
    appendChatMessage(chatMessage, true);
    showChatLoadingSpinner();
    let pastChatMessage = chatMessage;
    setChatMessage('');

    // Example logic for detecting certain meta tags
    let metatag, metatag_prefixes, metatag_suffixes, returnContext;
    if (pastChatMessage.trim().endsWith('**')) {
      metatag = ['title', 'author'];
      metatag_prefixes = ['\n\nTitle: ', '\nAuthor: '];
      metatag_suffixes = ['', '\n'];
      returnContext = true;
    } else {
      metatag = [];
      metatag_prefixes = [];
      metatag_suffixes = [];
      returnContext = false;
    }

    // Build up the conversation history for context
    let fullHistory = chatHistory
      .map((item) => {
        return item.isUserMessage
          ? `User: ${item.message}`
          : `Response: ${item.message}`;
      })
      .join('\n');

    // Basic credit usage logic
    let creditsUsed = (fullHistory.length + pastChatMessage.length) / 200;
    if (creditsUsed >= 80) {
      creditsUsed = 80;
    }

    // Avoid sending too large a history
    const maxLength = 15000 - chatMessage.length;
    const truncatedFullHistory = fullHistory.slice(-maxLength);
    fullHistory = truncatedFullHistory;

    try {
      setIsLoggedIn(Boolean(currentUser));
      // If user has enough credits, call the endpoint
      if (userData.credits >= 5) {
          // Assuming vectorVault is initialized and available
          if (vectorVault) {
            try {
              await vectorVault.getChatStream({
            text: pastChatMessage,
            history: fullHistory,
            summary: summary,
            get_context: getContext,
            n_context: nContext,
            return_context: returnContext,
                smart_history_search: historySearch, // Note: parameter name changed from history_search
            model: model,
            include_context_meta: includeContextMeta,
            metatag: metatag,
            metatag_prefixes: metatag_prefixes,
                metatag_suffixes: metatag_suffixes,
                vault: VECTOR_VAULT_CONFIG.vault, // Uses configured vault name
                temperature: 0 // Add temperature parameter
              }, (word) => {
                // This callback is called for each streaming chunk
              // Hide spinner once data actually begins streaming
                if (isChatLoading) {
                hideChatLoadingSpinner();
              }

                if (word === '!END') {
                    // End of stream
                  hideChatLoadingSpinner(); // Hide the loading spinner
                    setTempMessage(tempMessage => {
                      const newChatHistory = [
                        ...chatHistory,
                        { message: pastChatMessage, isUserMessage: true },
                        { message: tempMessage, isUserMessage: false }
                      ];
                      localStorage.setItem("chatHistory", JSON.stringify(newChatHistory));
                      setChatHistory(newChatHistory);

                      // Deduct used credits
                      const usedUp = creditsUsed + (tempMessage.length / 200);
                      const newCreds = userData.credits - usedUp;
                      const displayCreds = Math.round(newCreds * 100) / 100;
                      update(userRefdb, { credits: newCreds });
                      setUserCredits(displayCreds);

                      return '';
                    });
                  } else {
                    // Accumulate partial text
                    setTempMessage(tempMessage => {
                    const updatedTempMessage = `${tempMessage}${word}`;
                        // Remove the in-progress bot message, re-append with updated text
                        removeLastChatMessage();
                        appendChatMessage(updatedTempMessage, false);
                      return updatedTempMessage;
                    });
                  }
              });
            } catch (error) {
              console.error('VectorVault streaming error:', error);
              appendChatMessage(`Error: ${error.message}`, false);
              hideChatLoadingSpinner();
            }
          } else {
            console.error('VectorVault not initialized.');
            appendChatMessage('Error: VectorVault not initialized.', false);
            hideChatLoadingSpinner();
          }
      } else {
        // Not enough credits
        if (currentUser) {
          setShowUpgradeMessage(true);
          localStorage.setItem("showUp", "true");
        } else {
          appendChatMessage(`Insufficient credits. Sign in or Sign up at the top to continue.`, false);
          setShowUpgradeMessage(false);
          localStorage.setItem("showUp", "false");
          hideChatLoadingSpinner();
        }
      }
    } catch (error) {
      appendChatMessage(error.message, false);
      localStorage.setItem("chatHistory", JSON.stringify([...chatHistory, { message: error.message, isUserMessage: false }]));
    } finally {
      // any final cleanup if needed
    }
  };

  const clearChat = useCallback(() => {
    setChatHistory([]);
    localStorage.removeItem("chatHistory");
    localStorage.setItem("showUp", "false");
    setShowUpgradeMessage(false);
  }, [setShowUpgradeMessage, setChatHistory]);

  const handleTextareaKeydown = (e) => {
    // Send on Ctrl+Enter or Cmd+Enter
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      sendChatMessage();
    }
  };

  return (
    <>
      <div
        className="response-container"
        id="chat-container"
        style={{ ...styles.chatResponseContainer, paddingTop: "15px", marginRight: "-12px", marginLeft: "-12px"}}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <h3 style={{ ...styles.responseContainerH3, marginLeft: "-6px"}}>Chat:</h3>
          <button
            id="clear-chat"
            className="link-button"
            onClick={clearChat}
            style={{ marginTop: "-20px" }}
          >
            Clear
          </button>
          {showUpgradeMessage && <UpgradeMessage />}
        </div>

        {/* Display the chat history with code highlighting, etc. */}
        {chatHistory.flatMap((item, index) => {
          const segments = item.message.split(/(```[\s\S]*?```)/);
          return segments.map((segment, segmentIndex) =>
            renderChatSegment(segment, `${index}-${segmentIndex}`, item.isUserMessage)
          );
        })}

        <label htmlFor="chat-message" style={{ ...styles.yourmsgLabel, marginLeft: "-4px"}}>
          Your message:
        </label>
        <textarea
          placeholder="What is the meaning of life?"
          id="chat-message"
          rows={isMobile ? "8" : "5"}
          style={{ ...styles.textarea, marginLeft: "-8px", paddingRight: "6px", paddingLeft: "8px"}}
          value={chatMessage}
          onChange={(e) => setChatMessage(e.target.value)}
          onKeyDown={handleTextareaKeydown}
        />
        <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
          <button
            id="chat-submit"
            style={{ ...styles.button, marginLeft: "-6px"}}
            onClick={sendChatMessage}
          >
            Send
          </button>
          <div
            className="loading-spinner"
            id="loading-spinner"
            style={{
              ...styles.loadingSpinner,
              display: isChatLoading ? 'block' : 'none',
              paddingLeft: '20px'
            }}
          >
            <svg viewBox="0 0 50 50" style={styles.loadingSpinnerSvg}>
              <circle
                cx="25"
                cy="25"
                r="20"
                stroke="#007aff"
                strokeWidth="5"
                fill="none"
                style={styles.loadingSpinnerCircle}
              />
            </svg>
          </div>
        </div>
      </div>
    </>
  );
}

export default forwardRef(ChatContainer);
